import React from 'react';
import type { Editor } from '@tiptap/react';
import { 
  Bold, 
  Italic, 
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Link2,
  Code,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface TextFormattingSidebarProps {
  editor: Editor | null;
}

interface FormatButtonProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  isActive?: boolean;
  shortcut?: string;
}

function FormatButton({ icon: Icon, label, onClick, isActive, shortcut }: FormatButtonProps) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200',
            'hover:bg-sidebar-accent/60 hover:scale-110',
            'active:scale-95',
            isActive 
              ? 'bg-primary/20 text-primary ring-1 ring-primary/30' 
              : 'text-sidebar-foreground'
          )}
        >
          <Icon className="h-5 w-5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        <div className="flex flex-col">
          <span>{label}</span>
          {shortcut && <span className="text-xs text-muted-foreground">{shortcut}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function TextFormattingSidebar({ editor }: TextFormattingSidebarProps) {
  if (!editor) return null;

  const formatButtons = [
    {
      icon: Bold,
      label: 'Bold',
      shortcut: '⌘B',
      onClick: () => editor.chain().focus().toggleBold().run(),
      isActive: editor.isActive('bold'),
    },
    {
      icon: Italic,
      label: 'Italic',
      shortcut: '⌘I',
      onClick: () => editor.chain().focus().toggleItalic().run(),
      isActive: editor.isActive('italic'),
    },
    {
      icon: Strikethrough,
      label: 'Strikethrough',
      shortcut: '⌘⇧S',
      onClick: () => editor.chain().focus().toggleStrike().run(),
      isActive: editor.isActive('strike'),
    },
  ];

  const headingButtons = [
    {
      icon: Heading1,
      label: 'Heading 1',
      shortcut: '⌘⌥1',
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: editor.isActive('heading', { level: 1 }),
    },
    {
      icon: Heading2,
      label: 'Heading 2',
      shortcut: '⌘⌥2',
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: editor.isActive('heading', { level: 2 }),
    },
    {
      icon: Heading3,
      label: 'Heading 3',
      shortcut: '⌘⌥3',
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: editor.isActive('heading', { level: 3 }),
    },
  ];

  const listButtons = [
    {
      icon: List,
      label: 'Bullet List',
      shortcut: '⌘⇧8',
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      isActive: editor.isActive('bulletList'),
    },
    {
      icon: ListOrdered,
      label: 'Numbered List',
      shortcut: '⌘⇧7',
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: editor.isActive('orderedList'),
    },
    {
      icon: CheckSquare,
      label: 'Checklist',
      onClick: () => editor.chain().focus().toggleTaskList().run(),
      isActive: editor.isActive('taskList'),
    },
  ];

  const otherButtons = [
    {
      icon: Link2,
      label: 'Link',
      shortcut: '⌘K',
      onClick: () => {
        const url = window.prompt('Enter URL:');
        if (url) {
          editor.chain().focus().setLink({ href: url }).run();
        }
      },
      isActive: editor.isActive('link'),
    },
    {
      icon: Code,
      label: 'Code',
      shortcut: '⌘E',
      onClick: () => editor.chain().focus().toggleCode().run(),
      isActive: editor.isActive('code'),
    },
  ];

  return (
    <div className="flex flex-col items-center gap-1 px-3 animate-fade-in">
      {/* Text formatting */}
      <div className="flex flex-col items-center gap-1">
        {formatButtons.map(({ icon, label, shortcut, onClick, isActive }) => (
          <FormatButton
            key={label}
            icon={icon}
            label={label}
            shortcut={shortcut}
            onClick={onClick}
            isActive={isActive}
          />
        ))}
      </div>

      {/* Divider */}
      <div className="w-6 h-px bg-border/60 my-2" />

      {/* Headings */}
      <div className="flex flex-col items-center gap-1">
        {headingButtons.map(({ icon, label, shortcut, onClick, isActive }) => (
          <FormatButton
            key={label}
            icon={icon}
            label={label}
            shortcut={shortcut}
            onClick={onClick}
            isActive={isActive}
          />
        ))}
      </div>

      {/* Divider */}
      <div className="w-6 h-px bg-border/60 my-2" />

      {/* Lists */}
      <div className="flex flex-col items-center gap-1">
        {listButtons.map(({ icon, label, shortcut, onClick, isActive }) => (
          <FormatButton
            key={label}
            icon={icon}
            label={label}
            shortcut={shortcut}
            onClick={onClick}
            isActive={isActive}
          />
        ))}
      </div>

      {/* Divider */}
      <div className="w-6 h-px bg-border/60 my-2" />

      {/* Other */}
      <div className="flex flex-col items-center gap-1">
        {otherButtons.map(({ icon, label, shortcut, onClick, isActive }) => (
          <FormatButton
            key={label}
            icon={icon}
            label={label}
            shortcut={shortcut}
            onClick={onClick}
            isActive={isActive}
          />
        ))}
      </div>
    </div>
  );
}
